// Shareable configuration URL.
//
// The discover-sdk already owns the get/set-state + URL contract (url-state.ts):
//   v / ec / ic / bg / acc / pkg   -> variant, exterior/interior colour, background,
//                                     accessories, packages (lists are '.'-joined)
// On first load it reads these from `window.location.search` (NOT the hash),
// sanitises them against the metadata and uses them instead of the default
// configuration. This module only adapts the app to that contract:
//   - getShareParams / getShareUrl  build the link every share channel uses
//   - initUrlConfiguration          boot-time wiring (journey, view, live sync)
//
// `view` / `hs` (camera) are deliberately never shared or restored: the camera
// is driven by App's `activeTab`, so restoring an interior camera on the CONFIG
// tab would leave the two out of sync.
import {
  ConfigurationStore,
  LoaderState,
  configurationFromParams,
  configurationToParams,
  mergeUrlParams,
  useAccessoryStore,
  usePackageStore,
} from '@adloid-technologies/discover-sdk';
import { uimetadata } from './metadata';
import { journeyOf, setSelectedJourney } from './selectedJourney';

const VIEW_KEYS = ['view', 'hs'] as const;
// App/session params that only make sense for the person who opened the link.
const PRIVATE_KEYS = ['returnUrl', 'channel'] as const;

// Pre-2.0 share keys the SDK still reads; cleared once the compact keys are written
// so a stale `variantID=` can never mix with a newer `v=`.
const LEGACY_KEYS = [
  'modelId',
  'variantID',
  'exteriorColorID',
  'interiorColorID',
  'backgroundId',
  'accessoriesIDs',
  'packageIDs',
] as const;

// Captured at module load, i.e. before the SDK consumes the URL on first load.
const initialUrlConfiguration = configurationFromParams();

/**
 * The variant a shared URL asked for, or null when the page was opened without one
 * (or with a variant this link doesn't have, which the SDK ignores). A non-null
 * value means "someone shared this build": the presentation is skipped for it.
 */
export function getSharedVariantId(): string | null {
  const variantID = initialUrlConfiguration?.variantID;
  const modelId: string | undefined = (uimetadata as any)?.config?.model;
  const known = !!(modelId && variantID && (uimetadata as any)?.models?.[modelId]?.variants?.[variantID]);
  return known ? (variantID as string) : null;
}

/** The current configuration as SDK query params (no camera), empty values dropped. */
export function getShareParams(): URLSearchParams {
  const params = new URLSearchParams();
  const all = configurationToParams();
  for (const [key, value] of Object.entries(all)) {
    if ((VIEW_KEYS as readonly string[]).includes(key)) continue;
    if (value) params.set(key, value);
  }
  return params;
}

/**
 * The address bar's URL with the current configuration merged in. Built from the
 * live stores at call time (the address bar is debounced and paused mid-load),
 * keeps the hash route and its params (country, lang, dealerName, ...).
 */
export function getShareUrl(base: string = window.location.href): string {
  const url = new URL(base);
  for (const key of [...VIEW_KEYS, ...PRIVATE_KEYS]) url.searchParams.delete(key);
  for (const [key, value] of getShareParams()) url.searchParams.set(key, value);
  return url.toString();
}

/**
 * Call once, before the app mounts.
 *  1. Drops any camera params so the SDK restore / live sync never touches the view.
 *  2. Aligns the presentation journey with a restored variant (Corvette / Hummer).
 *  3. Keeps the address bar in sync with the configuration (reload + copy-from-bar).
 *
 * Step 3 mirrors the SDK's own `syncConfigurationToUrl` (same stores, same "only
 * once nothing is loading" gate, replaceState, debounced) but never writes the
 * camera, which the SDK helper has no option to leave out.
 */
export function initUrlConfiguration(): void {
  mergeUrlParams({ view: null, hs: null });

  const sharedVariantId = getSharedVariantId();
  if (sharedVariantId) setSelectedJourney(journeyOf(sharedVariantId));

  startUrlSync();
}

function startUrlSync(debounceMs = 250): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const write = () => {
    const { configuration, dynamicOptions } = ConfigurationStore.getState();
    const loader = LoaderState.getState();
    // Never write a half-applied state (a reload would restore it).
    if (
      !configuration ||
      !dynamicOptions?.enableAssets ||
      loader.isVariantLoading ||
      loader.isColorLoading ||
      loader.isAccessoryLoading
    ) {
      return;
    }
    const clear = Object.fromEntries([...LEGACY_KEYS, ...VIEW_KEYS].map((k) => [k, null]));
    mergeUrlParams({ ...clear, ...Object.fromEntries(getShareParams()) });
  };
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(write, debounceMs);
  };
  const unsubscribers = [
    ConfigurationStore.subscribe(schedule),
    useAccessoryStore.subscribe(schedule),
    usePackageStore.subscribe(schedule),
    LoaderState.subscribe(schedule),
  ];
  schedule();
  return () => {
    clearTimeout(timer);
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
}
